import React, { memo } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types'

import { VARIATION_TYPES } from 'Pods/ThreeDStudio/constants'
import useOpenVariation from 'Pods/ThreeDStudio/Hooks/useOpenVariation'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import useVariationMenu from 'Pods/ThreeDStudio/Hooks/useVariationMenu'
import DeleteConfirmModal from 'Pods/ThreeDStudio/Shared/DeleteConfirmModal/DeleteConfirmModal'
import VariationMenu from 'Pods/ThreeDStudio/Shared/VariationMenu/VariationMenu'

import Details from './Components/Details/Details'
import Thumbnail from './Components/Thumbnail/Thumbnail'

function TreeRow({
  variation,
  position = null,
  isActive = false,
  isMuted = false,
  isActionLoading,
  onSetAsMain,
  onDelete,
}) {
  const t = useTranslation()
  const { id, slug, providerModelId, outputAssets, title, prompt, createdAt } = variation
  const variationType = VARIATION_TYPES[providerModelId]
  const itemName = variationType?.label ?? title ?? prompt
  const { handleClick, handleKeyDown } = useOpenVariation({ id, slug, title: title ?? prompt })
  const menu = useVariationMenu({ id, itemName, onSetAsMain, onDelete })

  return (
    <>
      <div
        className={classnames(
          'variations-tree__row',
          isActive && 'variations-tree__row--active',
          isMuted && 'variations-tree__row--muted',
          menu.isMenuOpen && 'variations-tree__row--menu-open',
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-current={isActive || undefined}
      >
        <Thumbnail
          previewUrl={outputAssets?.[0]?.previewUrl}
          alt={title ?? prompt ?? variationType?.label ?? t('viewerSidebar.enhance.variations.variationPreview')}
          position={position}
        />
        <Details variation={variation} variationType={variationType} />
        <VariationMenu
          className="variations-tree__menu-trigger"
          createdAt={createdAt}
          isDisabled={isActionLoading}
          shouldClose={menu.shouldCloseDropdown}
          setShouldClose={menu.setShouldCloseDropdown}
          onOpenChange={menu.setIsMenuOpen}
          onSetAsMain={menu.handleSetAsMainClick}
          onDelete={menu.handleOpenDelete}
        />
      </div>
      {menu.isDeleteModalOpen && (
        <DeleteConfirmModal
          itemType="variation"
          itemName={itemName}
          isSubmitDisabled={isActionLoading}
          onSubmit={menu.handleConfirmDelete}
          onCancel={menu.handleCloseDelete}
        />
      )}
    </>
  )
}

TreeRow.propTypes = {
  variation: shape({
    id: oneOfType([string, number]).isRequired,
    slug: string,
    providerModelId: string,
    outputAssets: arrayOf(shape({
      previewUrl: string,
    })),
    title: string,
    prompt: string,
    createdAt: string,
  }).isRequired,
  position: number,
  isActive: bool,
  isMuted: bool,
  isActionLoading: bool.isRequired,
  onSetAsMain: func.isRequired,
  onDelete: func.isRequired,
}

export default memo(TreeRow)
